import React, { useContext, useEffect, useRef, useState } from "react";
import PagePreview from "./Preview";
import { useSelector } from "react-redux";
import { FullGridContext } from "..";
import PageContainer from "~/components/PageContainer";
import api from "~/services/api";
import { toast } from "react-toastify";
import ModalFilters, { EMPTY_GLOBAL_FILTERS } from "./ModalFilters";

function ExpireCell({ days, isExpired }) {
  if (days === null || days === undefined) {
    return <span className="text-gray-400">-</span>;
  }

  const value = Number(days);
  const tone =
    isExpired || value <= 15
      ? "bg-red-50 text-red-700"
      : value <= 30
      ? "bg-yellow-100 text-yellow-800"
      : "bg-green-100 text-green-800";

  return (
    <span className={`font-bold px-2 py-1 rounded-md ${tone}`}>
      {isExpired ? `Expired ${Math.abs(value)} day${Math.abs(value) === 1 ? "" : "s"} ago` : value === 0 ? "Expires today" : `${value} days`}
    </span>
  );
}

export default function FinancialGlobal() {
  const filial = useSelector((state) => state.auth.filial);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_GLOBAL_FILTERS);
  const latestRequest = useRef(0);
  const defaultGridHeader = [
    {
      title: "Registration Number",
      name: "registration_number",
      type: "text",
      filter: false,
    },
    {
      title: "Filial",
      name: "filial",
      type: "text",
      filter: false,
    },
    {
      title: "Category",
      name: "category",
      type: "text",
      filter: false,
    },
    {
      title: "Student Status",
      name: "student_status",
      type: "text",
      filter: false,
    },
    {
      title: "Name",
      name: "name",
      type: "text",
      filter: false,
    },
    {
      title: "Last Name",
      name: "last_name",
      type: "text",
      filter: false,
    },
    {
      title: "Session End Date",
      name: "session_end_date",
      type: "text",
      filter: false,
    },
    {
      title: "Session Expire in:",
      name: "days_to_expire",
      type: "text",
      filter: false,
    },
    {
      title: "Global Status",
      name: "status",
      type: "text",
      filter: false,
    },
  ];

  const {
    accessModule,
    gridData,
    handleOpened,
    limit,
    opened,
    orderBy,
    page,
    search,
    setGridData,
    setActiveFilters,
    setGridDetails,
    setLoadingData,
    setPage,
    setPages,
    setSearch,
    setTotalRows,
  } = useContext(FullGridContext);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "global_financial"
  );

  useEffect(() => {
    setSearch("");
    setPage(1);
    setActiveFilters([]);
  }, []);

  useEffect(() => {
    setFilters(EMPTY_GLOBAL_FILTERS);
    setPage(1);
  }, [filial?.id]);

  function handleApplyFilters(nextFilters) {
    setFilters(nextFilters);
    setPage(1);
  }

  async function loader() {
    const requestId = ++latestRequest.current;
    setLoadingData(true);
    try {
      const orderFix = orderBy?.column ?? "name";
      const orderDir = orderBy ? (orderBy.asc ? "ASC" : "DESC") : "ASC";
      const params = new URLSearchParams({
        limit: String(limit),
        page: String(page),
        orderBy: orderFix,
        orderASC: orderDir,
        search: search?.value ?? "",
      });
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.set(key, String(value));
        }
      });
      const { data } = await api.get(
        `/contractsessions?${params.toString()}`
      );
      if (requestId !== latestRequest.current) return;
      const rows = data.rows ?? [];
      const totalRows = data.totalRows ?? rows.length;
      const pages = Math.ceil(totalRows / limit) || 1;
      setPages(pages);
      setGridDetails({ totalRows, pages });
      setTotalRows(totalRows);

      const gridDataValues = rows.map((session, index) => ({
        show: true,
        id: session.id,
        fields: [
          session.registration_number ?? "-",
          session.filial?.name ?? "-",
          session.category ?? "-",
          session.student_status ?? "-",
          session.name ?? "-",
          session.last_name ?? "-",
          session.session_end_date ?? "-",
          <ExpireCell
            days={session.days_to_expire}
            isExpired={session.is_expired}
          />,
          session.status ?? "Global",
        ],
        selectable: false,
        canceled: null,
        page: Math.ceil((index + 1) / limit),
      }));

      setGridData(gridDataValues);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      console.log(err);
      toast.error(err?.response?.data?.error ?? "Failed to load Global sessions.");
    }
    if (requestId === latestRequest.current) setLoadingData(false);
  }

  useEffect(() => {
    if (!opened) {
      loader();
    }
  }, [opened, filial, orderBy, search, limit, page, filters]);

  const activeFilterCount = Object.values(filters).filter((value) => value !== "").length;

  return (
    <>
      <ModalFilters
        opened={filtersModalOpen}
        onClose={() => setFiltersModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        value={filters}
        selectedFilial={filial}
      />
      <PageContainer
        FullGridContext={FullGridContext}
        PagePreview={PagePreview}
        pageAccess={pageAccess}
        handleNew={false}
        defaultGridHeader={defaultGridHeader}
        containerClassName="min-w-0 w-full"
        gridHorizontalScroll
        gridMinWidthClassName="min-w-[1100px] w-full"
        filtersModal={{
          onOpen: () => setFiltersModalOpen(true),
          viewSelect: activeFilterCount > 0 && (
            <span className="text-xs text-sky-700 font-semibold">{activeFilterCount} active filter{activeFilterCount > 1 ? "s" : ""}</span>
          ),
        }}
      />
    </>
  );
}
